import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { complianceChecklistApi } from '../lib/api';
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  category?: string;
}

interface CompletedItem {
  itemId: string;
  checked: boolean;
  notes?: string;
}

interface ChecklistCompletionProps {
  reportId: string;
}

function ChecklistSection({
  checklist,
  completion,
  reportId,
}: {
  checklist: { id: string; name: string; description?: string | null; items: ChecklistItem[] };
  completion?: { completedItems: CompletedItem[]; isComplete: boolean };
  reportId: string;
}) {
  const queryClient = useQueryClient();
  const items: ChecklistItem[] = checklist.items || [];

  const [completedItems, setCompletedItems] = useState<CompletedItem[]>(() => {
    if (completion?.completedItems) {
      return completion.completedItems;
    }
    return items.map((item) => ({ itemId: item.id, checked: false }));
  });

  // Reset when completion data changes
  useEffect(() => {
    if (completion?.completedItems) {
      setCompletedItems(completion.completedItems);
    }
  }, [completion]);

  const saveMutation = useMutation({
    mutationFn: () =>
      complianceChecklistApi.updateCompletion(reportId, checklist.id, { completedItems }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportChecklists', reportId] });
      toast.success('Checklist saved');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save checklist');
    },
  });

  const toggleItem = (itemId: string) => {
    setCompletedItems((prev) => {
      const existing = prev.find((ci) => ci.itemId === itemId);
      if (existing) {
        return prev.map((ci) =>
          ci.itemId === itemId ? { ...ci, checked: !ci.checked } : ci
        );
      }
      return [...prev, { itemId, checked: true }];
    });
  };

  const updateNotes = (itemId: string, notes: string) => {
    setCompletedItems((prev) => {
      const existing = prev.find((ci) => ci.itemId === itemId);
      if (existing) {
        return prev.map((ci) =>
          ci.itemId === itemId ? { ...ci, notes } : ci
        );
      }
      return [...prev, { itemId, checked: false, notes }];
    });
  };

  const checkedCount = completedItems.filter((ci) => ci.checked).length;
  const requiredCount = items.filter((i) => i.required).length;
  const requiredComplete = items
    .filter((i) => i.required)
    .every((i) => completedItems.find((ci) => ci.itemId === i.id)?.checked);

  // Group items by category
  const categories = new Map<string, ChecklistItem[]>();
  items.forEach((item) => {
    const cat = item.category || 'General';
    if (!categories.has(cat)) categories.set(cat, []);
    categories.get(cat)!.push(item);
  });

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div>
          <h4 className="text-sm font-semibold text-navy-900">{checklist.name}</h4>
          {checklist.description && (
            <p className="text-xs text-gray-500">{checklist.description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {checkedCount}/{items.length} complete
          </span>
          {completion?.isComplete && (
            <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              <CheckCircleIcon className="h-3 w-3" />
              Done
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-1">
        {Array.from(categories.entries()).map(([category, catItems]) => (
          <div key={category}>
            {categories.size > 1 && (
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mt-2 mb-1">{category}</p>
            )}
            {catItems.map((item) => {
              const ci = completedItems.find((c) => c.itemId === item.id);
              const isChecked = ci?.checked || false;

              return (
                <div key={item.id} className="flex items-start gap-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(item.id)}
                    className="mt-0.5 rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
                  />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm ${isChecked ? 'text-gray-400 line-through' : 'text-gray-700'} ${item.required ? 'font-medium' : ''}`}>
                      {item.label}
                      {item.required && <span className="text-red-500 ml-0.5">*</span>}
                    </span>
                    {isChecked && (
                      <input
                        type="text"
                        value={ci?.notes || ''}
                        onChange={(e) => updateNotes(item.id, e.target.value)}
                        placeholder="Add notes..."
                        className="block w-full mt-1 text-xs border-0 border-b border-gray-200 px-0 py-0.5 text-gray-500 focus:border-ocean-500 focus:ring-0"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
        <p className="text-xs text-gray-500">
          {requiredCount > 0 && !requiredComplete
            ? `${requiredCount - items.filter((i) => i.required && completedItems.find((ci) => ci.itemId === i.id)?.checked).length} required items remaining`
            : requiredCount > 0
            ? 'All required items complete'
            : ''}
        </p>
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-ocean-600 text-white text-xs font-medium rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-colors"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save Progress'}
        </button>
      </div>
    </div>
  );
}

export default function ChecklistCompletion({ reportId }: ChecklistCompletionProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['reportChecklists', reportId],
    queryFn: async () => {
      const response = await complianceChecklistApi.getReportCompletions(reportId);
      return response.data;
    },
  });

  if (isLoading) {
    return <p className="text-sm text-gray-500">Loading checklists...</p>;
  }

  const checklists = data?.checklists || [];
  const completions = data?.completions || [];

  if (checklists.length === 0) {
    return null; // No checklists for this team
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ClipboardDocumentCheckIcon className="h-5 w-5 text-ocean-600" />
        <h3 className="text-lg font-semibold text-navy-900">Compliance Checklists</h3>
      </div>
      {checklists.map((checklist: any) => {
        const completion = completions.find((c: any) => c.checklistId === checklist.id);
        return (
          <ChecklistSection
            key={checklist.id}
            checklist={checklist}
            completion={completion}
            reportId={reportId}
          />
        );
      })}
    </div>
  );
}
